import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, ImagePlus, Video, X, Loader2, ExternalLink } from "lucide-react";
import { fileToDataUrl } from "@/components/PhotoDrop";

/**
 * DM inbox — text, image attachments, and Jitsi video-call invitations.
 *
 * Video call: clicking the camera button creates a unique Jitsi room URL and posts a
 * message with `video_call_url` set. Both participants can click "Join call" to open
 * the room in a new tab. Jitsi is free, no API key required.
 */
export default function Messages() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [attach, setAttach] = useState(null);   // { url } for image attachments
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvos = () =>
    api.get("/messages/conversations").then((r) => setConversations(r.data));
  useEffect(() => { loadConvos(); }, []);

  useEffect(() => {
    if (!otherId) { setMessages([]); setOther(null); return; }
    api.get(`/messages/with/${otherId}`).then((r) => setMessages(r.data));
    api.get(`/users/${otherId}`).then((r) => setOther(r.data.user));
    const intv = setInterval(() => {
      api.get(`/messages/with/${otherId}`).then((r) => setMessages(r.data));
    }, 4000);
    return () => clearInterval(intv);
  }, [otherId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const uploadImage = async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file, 1600);
      const { data } = await api.post("/uploads/image", { data_url: dataUrl });
      if (!data?.url) throw new Error("Upload succeeded but no URL was returned");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setAttach({ url });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !attach) || !otherId) return;
    setBusy(true);
    try {
      await api.post("/messages", {
        to_user_id: otherId,
        text,
        image_url: attach?.url || "",
      });
      setText(""); setAttach(null);
      const r = await api.get(`/messages/with/${otherId}`);
      setMessages(r.data);
      loadConvos();
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const startVideoCall = async () => {
    if (!otherId) return;
    // Unique, non-guessable room slug — 8 hex chars is plenty for 1:1 rooms
    const room = `hoomanz-${crypto.randomUUID().slice(0, 8)}`;
    const callUrl = `https://meet.jit.si/${room}#config.prejoinPageEnabled=false`;
    setBusy(true);
    try {
      await api.post("/messages", {
        to_user_id: otherId,
        text: "📹 Started a video call — tap Join to hop in.",
        video_call_url: callUrl,
      });
      const r = await api.get(`/messages/with/${otherId}`);
      setMessages(r.data);
      loadConvos();
      // Auto-open the call for the caller
      window.open(callUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8" data-testid="messages-page">
      <p className="overline mb-3">Messages</p>
      <h1 className="h-display text-3xl mb-6">Talk to real Hoomanz.</h1>

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        <aside className="card p-3" data-testid="messages-conversations">
          {conversations.length === 0 && (
            <p className="text-sm text-[#8A8A8A] p-3">
              No conversations yet. Message a Hooman from their profile.
            </p>
          )}
          {conversations.map((c) => {
            const preview = c.last_message.video_call_url
              ? "📹 Video call"
              : c.last_message.image_url
              ? "📷 Photo"
              : c.last_message.text;
            return (
              <Link
                key={c.convo_id}
                to={`/messages/${c.other.id}`}
                className={`flex items-center gap-3 p-3 rounded-2xl hover:bg-[#F2F2F0] ${otherId === c.other.id ? "bg-[#F2F2F0]" : ""}`}
                data-testid={`convo-${c.other.id}`}
              >
                {c.other.avatar ? (
                  <img src={c.other.avatar} className="h-10 w-10 rounded-full object-cover" alt=""/>
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#E5E5E3]"/>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.other.name}</p>
                  <p className="text-xs text-[#8A8A8A] truncate">{preview}</p>
                </div>
              </Link>
            );
          })}
        </aside>

        <section className="card p-0 flex flex-col h-[70vh]">
          {!otherId ? (
            <div className="m-auto text-[#8A8A8A] text-center">
              Pick a conversation, or open a Hooman&apos;s profile and tap Message.
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[#E5E5E3] flex items-center gap-3" data-testid="messages-header">
                {other?.avatar && (
                  <img src={other.avatar} className="h-10 w-10 rounded-full object-cover" alt=""/>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{other?.name || "…"}</p>
                  <p className="text-xs text-[#8A8A8A] truncate">{other?.category}</p>
                </div>
                <button
                  type="button"
                  onClick={startVideoCall}
                  disabled={busy}
                  className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1.5"
                  title="Start a video call with this Hooman"
                  data-testid="messages-start-call"
                >
                  <Video size={14}/> Video call
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="messages-thread">
                {messages.map((m) => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm break-words ${mine ? "ml-auto bg-[#0A0A0A] text-white" : "bg-[#F2F2F0] text-[#0A0A0A]"}`}
                      data-testid={`msg-${m.id}`}
                    >
                      {m.image_url && (
                        <img
                          src={m.image_url}
                          alt=""
                          data-lightbox
                          className="rounded-xl mb-1.5 max-w-full max-h-[280px] object-cover cursor-zoom-in"
                          data-testid={`msg-img-${m.id}`}
                        />
                      )}
                      {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                      {m.video_call_url && (
                        <a
                          href={m.video_call_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${mine ? "bg-white text-[#0A0A0A]" : "bg-[#0057FF] text-white"} hover:opacity-90`}
                          data-testid={`msg-join-call-${m.id}`}
                        >
                          <Video size={12}/> Join call <ExternalLink size={10}/>
                        </a>
                      )}
                      <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#8A8A8A]"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  );
                })}
                <div ref={bottomRef}/>
              </div>

              {attach && (
                <div className="border-t border-[#E5E5E3] px-3 py-2 flex items-center gap-2" data-testid="messages-attach-preview">
                  <img src={attach.url} alt="" className="h-12 w-12 object-cover rounded-lg"/>
                  <p className="text-xs text-[#4A4A4A] flex-1 truncate">Photo ready to send</p>
                  <button
                    type="button"
                    onClick={() => setAttach(null)}
                    className="btn-ghost !py-1 !px-2 text-xs"
                    data-testid="messages-attach-remove"
                  >
                    <X size={12}/>
                  </button>
                </div>
              )}

              <form onSubmit={send} className="border-t border-[#E5E5E3] p-3 flex gap-2 items-center" data-testid="messages-form">
                <input
                  type="file"
                  ref={fileRef}
                  accept="image/*"
                  hidden
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                  data-testid="messages-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="btn-ghost !p-2"
                  title="Attach photo"
                  disabled={busy}
                  data-testid="messages-attach-btn"
                >
                  {busy && !attach ? <Loader2 size={16} className="animate-spin"/> : <ImagePlus size={16}/>}
                </button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Say hi"
                  className="input flex-1"
                  data-testid="messages-input"
                />
                <button
                  type="submit"
                  disabled={(!text.trim() && !attach) || busy}
                  className="btn-primary !px-5"
                  data-testid="messages-send"
                >
                  {busy && attach ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
